import React, { useEffect, useRef, useState } from 'react';
import { sendUnity } from './graphicsQuality';

export function movementVector(dx: number, dy: number, radius: number) {
  if (radius <= 0) return { x: 0, y: 0 };
  const length = Math.hypot(dx, dy);
  if (length < radius * 0.12) return { x: 0, y: 0 };
  const divisor = Math.max(radius, length);
  return { x: dx / divisor, y: -dy / divisor };
}

/** Uses UPC's existing MoveInput contract; camera drag remains on the canvas. */
const MobileWebMovement: React.FC<{ disabled: boolean }> = ({ disabled }) => {
  const target = useRef<HTMLDivElement>(null);
  const pointer = useRef<number | null>(null);
  const frame = useRef<number | null>(null);
  const pending = useRef({ x: 0, y: 0 });
  const [knob, setKnob] = useState({ x: 0, y: 0 });

  const reset = () => {
    if (frame.current !== null) window.cancelAnimationFrame(frame.current);
    frame.current = null;
    const id = pointer.current;
    pointer.current = null;
    if (id !== null && target.current?.hasPointerCapture?.(id)) target.current.releasePointerCapture(id);
    pending.current = { x: 0, y: 0 };
    setKnob(pending.current);
    if (id !== null) sendUnity('MoveInput', pending.current);
  };

  useEffect(() => {
    if (disabled) reset();
    const hide = () => { if (document.hidden) reset(); };
    window.addEventListener('blur', reset);
    document.addEventListener('visibilitychange', hide);
    return () => {
      reset();
      window.removeEventListener('blur', reset);
      document.removeEventListener('visibilitychange', hide);
    };
    // The handlers use refs, so they always clear the current movement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  const update = (event: React.PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const radius = Math.min(rect.width, rect.height) * 0.35;
    pending.current = movementVector(event.clientX - rect.left - rect.width / 2,
      event.clientY - rect.top - rect.height / 2, radius);
    setKnob(pending.current);
    if (frame.current === null) frame.current = window.requestAnimationFrame(() => {
      frame.current = null;
      sendUnity('MoveInput', pending.current);
    });
  };

  return <div ref={target} role="group" aria-label="Touch movement control" aria-disabled={disabled}
    className="absolute z-[6] rounded-full border border-white/50 bg-black/30"
    style={{ width: 128, height: 128, left: 'max(16px, env(safe-area-inset-left))',
      bottom: 'calc(100px + env(safe-area-inset-bottom))', touchAction: 'none', userSelect: 'none',
      opacity: disabled ? 0 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
    onClick={event => event.stopPropagation()}
    onPointerDown={event => {
      if (disabled || pointer.current !== null || event.button !== 0) return;
      event.preventDefault(); event.stopPropagation();
      pointer.current = event.pointerId;
      event.currentTarget.setPointerCapture(event.pointerId);
      update(event);
    }}
    onPointerMove={event => {
      if (pointer.current !== event.pointerId) return;
      event.preventDefault(); event.stopPropagation(); update(event);
    }}
    onPointerUp={event => { if (pointer.current === event.pointerId) { event.stopPropagation(); reset(); } }}
    onPointerCancel={event => { if (pointer.current === event.pointerId) reset(); }}
    onLostPointerCapture={event => { if (pointer.current === event.pointerId) reset(); }}>
    <div className="absolute rounded-full bg-white/80 pointer-events-none"
      style={{ width: 44, height: 44, left: '50%', top: '50%',
        transform: `translate(calc(-50% + ${knob.x * 44.8}px), calc(-50% - ${knob.y * 44.8}px))` }} />
  </div>;
};

export default MobileWebMovement;
